import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const VendorDetailPanel = ({ 
  vendor, 
  isOpen, 
  onClose, 
  onSave, 
  onDelete,
  mode = 'edit' // 'edit', 'create', 'view'
}) => {
  const [formData, setFormData] = useState({
    companyName: '',
    tradingName: '',
    contactName: '',
    email: '',
    phone: '',
    accountReference: '',
    vatNumber: '',
    vatVerified: false,
    addressLine1: '',
    addressLine2: '',
    city: '',
    county: '',
    postcode: '',
    country: 'United Kingdom',
    paymentTerms: '',
    creditLimit: '',
    accountType: 'vendor',
    isActive: true,
    notes: ''
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const countryOptions = [
    { value: 'United Kingdom', label: 'United Kingdom' },
    { value: 'Ireland', label: 'Ireland' },
    { value: 'France', label: 'France' },
    { value: 'Germany', label: 'Germany' },
    { value: 'Netherlands', label: 'Netherlands' },
    { value: 'Belgium', label: 'Belgium' },
    { value: 'Spain', label: 'Spain' },
    { value: 'Italy', label: 'Italy' }
  ];

  const paymentTermsOptions = [
    { value: '', label: 'Select payment terms' },
    { value: 'NET7', label: 'Net 7 days' },
    { value: 'NET14', label: 'Net 14 days' },
    { value: 'NET30', label: 'Net 30 days' },
    { value: 'NET60', label: 'Net 60 days' },
    { value: 'COD', label: 'Cash on Delivery' },
    { value: 'PREPAID', label: 'Prepaid' }
  ];

  const accountTypeOptions = [
    { value: 'vendor', label: 'Vendor' },
    { value: 'supplier', label: 'Supplier' },
    { value: 'both', label: 'Vendor & Supplier' }
  ];

  useEffect(() => {
    if (vendor && mode !== 'create') {
      setFormData({
        companyName: vendor?.companyName || '',
        tradingName: vendor?.tradingName || '',
        contactName: vendor?.contactName || '',
        email: vendor?.email || '',
        phone: vendor?.phone || '',
        accountReference: vendor?.accountReference || '',
        vatNumber: vendor?.vatNumber || '',
        vatVerified: vendor?.vatVerified || false,
        addressLine1: vendor?.addressLine1 || '',
        addressLine2: vendor?.addressLine2 || '',
        city: vendor?.city || '',
        county: vendor?.county || '',
        postcode: vendor?.postcode || '',
        country: vendor?.country || 'United Kingdom',
        paymentTerms: vendor?.paymentTerms || '',
        creditLimit: vendor?.creditLimit || '',
        accountType: vendor?.accountType || 'vendor',
        isActive: vendor?.isActive !== false,
        notes: vendor?.notes || ''
      });
    } else if (mode === 'create') {
      // Reset form for new vendor
      setFormData({
        companyName: '',
        tradingName: '',
        contactName: '',
        email: '',
        phone: '',
        accountReference: '',
        vatNumber: '',
        vatVerified: false,
        addressLine1: '',
        addressLine2: '',
        city: '',
        county: '',
        postcode: '',
        country: 'United Kingdom',
        paymentTerms: '',
        creditLimit: '',
        accountType: 'vendor',
        isActive: true,
        notes: ''
      });
    }
    setErrors({});
  }, [vendor, mode]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.companyName?.trim()) {
      newErrors.companyName = 'Company name is required';
    }

    if (!formData?.accountReference?.trim()) {
      newErrors.accountReference = 'Account reference is required';
    }

    if (formData?.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (formData?.vatNumber && !/^GB\d{9}$/?.test(formData?.vatNumber?.replace(/\s/g, ''))) {
      newErrors.vatNumber = 'Please enter a valid UK VAT number (GB123456789)';
    }

    if (formData?.postcode && formData?.country === 'United Kingdom') {
      const ukPostcodeRegex = /^[A-Z]{1,2}[0-9][A-Z0-9]?\s?[0-9][A-Z]{2}$/i;
      if (!ukPostcodeRegex?.test(formData?.postcode)) {
        newErrors.postcode = 'Please enter a valid UK postcode';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      await onSave({
        ...formData,
        id: vendor?.id || Date.now(),
        lastUpdated: new Date()?.toISOString()
      });
      onClose();
    } catch (error) {
      console.error('Error saving vendor:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this vendor? This action cannot be undone.')) {
      setIsLoading(true);
      try {
        await onDelete(vendor?.id);
        onClose();
      } catch (error) {
        console.error('Error deleting vendor:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  if (!isOpen) return null;

  const isReadOnly = mode === 'view';
  const title = mode === 'create' ? 'Add New Vendor' : mode === 'view' ? 'Vendor Details' : 'Edit Vendor';

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
      {/* Panel */}
      <div className="relative ml-auto w-full max-w-2xl bg-card border-l border-border shadow-moderate overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="User" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">{title}</h2>
              <p className="text-sm text-muted-foreground">
                {mode === 'create' ? 'Create a new vendor record' : 'Manage vendor information and settings'}
              </p>
            </div>
          </div>
          
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto h-[calc(100vh-140px)]">
          <div className="p-6 space-y-8">
            {/* Company Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
                <Icon name="Building" size={18} />
                <span>Company Information</span>
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Company Name"
                  required
                  value={formData?.companyName}
                  onChange={(e) => handleInputChange('companyName', e?.target?.value)}
                  error={errors?.companyName}
                  disabled={isReadOnly}
                  placeholder="Enter company name"
                />
                
                <Input
                  label="Trading Name"
                  value={formData?.tradingName}
                  onChange={(e) => handleInputChange('tradingName', e?.target?.value)}
                  disabled={isReadOnly}
                  placeholder="Enter trading name (if different)"
                />
                
                <Input
                  label="Account Reference"
                  required
                  value={formData?.accountReference}
                  onChange={(e) => handleInputChange('accountReference', e?.target?.value)}
                  error={errors?.accountReference}
                  disabled={isReadOnly}
                  placeholder="e.g., VEND001"
                />
                
                <Select
                  label="Account Type"
                  options={accountTypeOptions}
                  value={formData?.accountType}
                  onChange={(value) => handleInputChange('accountType', value)}
                  disabled={isReadOnly}
                />
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
                <Icon name="Phone" size={18} />
                <span>Contact Information</span>
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Contact Name"
                  value={formData?.contactName}
                  onChange={(e) => handleInputChange('contactName', e?.target?.value)}
                  disabled={isReadOnly}
                  placeholder="Enter contact person name"
                />
                
                <Input
                  label="Email Address"
                  type="email"
                  value={formData?.email}
                  onChange={(e) => handleInputChange('email', e?.target?.value)}
                  error={errors?.email}
                  disabled={isReadOnly}
                  placeholder="Enter email address"
                />
                
                <Input
                  label="Phone Number"
                  type="tel"
                  value={formData?.phone}
                  onChange={(e) => handleInputChange('phone', e?.target?.value)}
                  disabled={isReadOnly}
                  placeholder="Enter phone number"
                />
              </div>
            </div>

            {/* Address Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
                <Icon name="MapPin" size={18} />
                <span>Address Information</span>
              </h3>
              
              <div className="space-y-4">
                <Input
                  label="Address Line 1"
                  value={formData?.addressLine1}
                  onChange={(e) => handleInputChange('addressLine1', e?.target?.value)}
                  disabled={isReadOnly}
                  placeholder="Enter street address"
                />
                
                <Input
                  label="Address Line 2"
                  value={formData?.addressLine2}
                  onChange={(e) => handleInputChange('addressLine2', e?.target?.value)}
                  disabled={isReadOnly}
                  placeholder="Enter additional address info (optional)"
                />
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Input
                    label="City"
                    value={formData?.city}
                    onChange={(e) => handleInputChange('city', e?.target?.value)}
                    disabled={isReadOnly}
                    placeholder="Enter city"
                  />
                  
                  <Input
                    label="County"
                    value={formData?.county}
                    onChange={(e) => handleInputChange('county', e?.target?.value)}
                    disabled={isReadOnly}
                    placeholder="Enter county"
                  />
                  
                  <Input
                    label="Postcode"
                    value={formData?.postcode}
                    onChange={(e) => handleInputChange('postcode', e?.target?.value)}
                    error={errors?.postcode}
                    disabled={isReadOnly}
                    placeholder="Enter postcode"
                  />
                </div>
                
                <Select
                  label="Country"
                  options={countryOptions}
                  value={formData?.country}
                  onChange={(value) => handleInputChange('country', value)}
                  disabled={isReadOnly}
                />
              </div>
            </div>

            {/* VAT & Financial Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
                <Icon name="Receipt" size={18} />
                <span>VAT &amp; Financial Information</span>
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Input
                    label="VAT Number"
                    value={formData?.vatNumber}
                    onChange={(e) => handleInputChange('vatNumber', e?.target?.value)}
                    error={errors?.vatNumber}
                    disabled={isReadOnly}
                    placeholder="GB123456789"
                  />
                  {!isReadOnly && (
                    <Checkbox
                      label="VAT number verified"
                      checked={formData?.vatVerified}
                      onChange={(e) => handleInputChange('vatVerified', e?.target?.checked)}
                    />
                  )}
                </div>
                
                <Select
                  label="Payment Terms"
                  options={paymentTermsOptions}
                  value={formData?.paymentTerms}
                  onChange={(value) => handleInputChange('paymentTerms', value)}
                  disabled={isReadOnly}
                />
                
                <Input
                  label="Credit Limit (£)"
                  type="number"
                  value={formData?.creditLimit}
                  onChange={(e) => handleInputChange('creditLimit', e?.target?.value)}
                  disabled={isReadOnly}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>

            {/* Additional Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
                <Icon name="FileText" size={18} />
                <span>Additional Information</span>
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <Checkbox
                    label="Active vendor"
                    description="Inactive vendors won't appear in dropdowns"
                    checked={formData?.isActive}
                    onChange={(e) => handleInputChange('isActive', e?.target?.checked)}
                    disabled={isReadOnly}
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Notes</label>
                  <textarea
                    value={formData?.notes}
                    onChange={(e) => handleInputChange('notes', e?.target?.value)}
                    disabled={isReadOnly}
                    placeholder="Enter any additional notes about this vendor..."
                    rows={4}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border bg-muted/20">
          <div className="flex items-center space-x-3">
            {mode === 'edit' && vendor && (
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={isLoading}
                iconName="Trash2"
                iconPosition="left"
              >
                Delete Vendor
              </Button>
            )}
          </div>
          
          <div className="flex items-center space-x-3">
            <Button variant="outline" onClick={onClose} disabled={isLoading}>
              {isReadOnly ? 'Close' : 'Cancel'}
            </Button>
            
            {!isReadOnly && (
              <Button
                onClick={handleSave}
                loading={isLoading}
                iconName="Save"
                iconPosition="left"
              >
                {mode === 'create' ? 'Create Vendor' : 'Save Changes'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorDetailPanel;